import {
  courseGroupingUserSelectors,
  courseSelectors,
  courseUserSelectors,
  createAppSelector,
} from "./base";
import { selectCourseForSectionId, selectCurrentCourse } from "./current";
import { selectIsCurrentOrgUserDisabled, selectIsOrgUserDisabled } from "./org";

import type { CourseGroupingUserType, CourseType, CourseUserType } from "components/server-types";
import { getEnabledCourseRole } from "utils/roles";

// Course-admin status is derived from a CourseUser row for the course or a CourseGroupingUser
// row for the course's grouping. Org-disabled membership contributes no course role
// (mirrors CourseUser / CourseGroupingUser.enabled()).
export const getCourseRole = (
  courseUsers: CourseUserType[],
  courseGroupingUsers: CourseGroupingUserType[],
  course: CourseType | null,
  isOrgUserDisabled = false,
) => {
  if (!course) {
    return null;
  }

  const courseUser = courseUsers.find((cu) => cu.course_id === course.id);
  if (courseUser) {
    return getEnabledCourseRole(courseUser, isOrgUserDisabled);
  }

  const groupingUser = courseGroupingUsers.find(
    (gu) => gu.course_grouping_id === course.grouping_id,
  );
  return getEnabledCourseRole(groupingUser ?? null, isOrgUserDisabled);
};

export const selectCurrentCourseRole = createAppSelector(
  [
    courseUserSelectors.filterForCurrentUser,
    courseGroupingUserSelectors.filterForCurrentUser,
    selectCurrentCourse,
    selectIsCurrentOrgUserDisabled,
  ],
  (courseUsers, courseGroupingUsers, course, isOrgUserDisabled) =>
    getCourseRole(courseUsers, courseGroupingUsers, course, isOrgUserDisabled),
);

export const selectCourseRoleByUserId = createAppSelector(
  [
    courseUserSelectors.filterByUserId,
    courseGroupingUserSelectors.filterByUserId,
    selectCurrentCourse,
    selectIsOrgUserDisabled,
  ],
  (courseUsers, courseGroupingUsers, course, isOrgUserDisabled) =>
    getCourseRole(courseUsers, courseGroupingUsers, course, isOrgUserDisabled),
);

export const selectCourseRoleByCourseId = createAppSelector(
  [
    courseUserSelectors.filterForCurrentUser,
    courseGroupingUserSelectors.filterForCurrentUser,
    courseSelectors.selectById,
    selectIsCurrentOrgUserDisabled,
  ],
  (courseUsers, courseGroupingUsers, course, isOrgUserDisabled) =>
    getCourseRole(courseUsers, courseGroupingUsers, course, isOrgUserDisabled),
);

export const selectCourseRoleBySectionId = createAppSelector(
  [
    courseUserSelectors.filterForCurrentUser,
    courseGroupingUserSelectors.filterForCurrentUser,
    selectCourseForSectionId,
    selectIsCurrentOrgUserDisabled,
  ],
  (courseUsers, courseGroupingUsers, course, isOrgUserDisabled) =>
    getCourseRole(courseUsers, courseGroupingUsers, course, isOrgUserDisabled),
);

// True if the user has any CourseUser rows. Course-admin status is now derived from
// CourseUser presence rather than an OrgUser.role of "course_admin".
export const selectIsUserACourseAdmin = createAppSelector(
  [courseUserSelectors.filterByUserId],
  (courseUsers) => courseUsers.length > 0,
);
